import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './DashboardApprovalPanel.css';

const RemotePoPreview = React.lazy(() => import('erpApp/PurchaseOrderPreviewMfe'));

/** ERP 결재 API는 반드시 현재 사이트 오리진의 /api 프록시를 탄다. */
function approvalUrl(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  return new URL(`/api/erp_approval${p}`, window.location.origin).toString();
}


const KIND_LABEL = {
  leave: '연차·휴가',
  purchase_order: '구매발주',
  quote: '견적서',
  order: '주문서',
};

/** erp_leave LEAVE_TYPES 표기와 맞춤 */
const LEAVE_TYPE_KO = {
  annual: '연차',
  half_am: '오전반차',
  half_pm: '오후반차',
  sick: '병가',
  special: '특별휴가',
  public: '공가',
};

const LEAVE_TYPE_EMOJI = {
  annual: '🏖️',
  half_am: '🌤️',
  half_pm: '🌆',
  sick: '🤒',
  special: '✨',
  public: '🏛️',
};

/** 제목 줄: 🏖️ 김장범 연차 신청 1일 신청 … */
function formatLeaveApprovalHeadline(it) {
  const d = it.detail || {};
  const name = String(d.requester_name || '').trim();
  const email = String(it.requester_email || '').trim();
  const who = name || (email ? email.split('@')[0] : '') || '신청자';
  const lt = String(d.leave_type || 'annual');
  const typeKo = LEAVE_TYPE_KO[lt] || '휴가';
  const emoji = LEAVE_TYPE_EMOJI[lt] || '📋';
  if (lt === 'public') {
    return `${emoji} ${who} ${typeKo} 신청 (연차 미차감)`;
  }
  let qty = '—';
  if (d.use_days != null && d.use_days !== '') {
    const n = Number(d.use_days);
    if (Number.isFinite(n)) {
      qty = n % 1 === 0 ? String(Math.trunc(n)) : String(n);
    }
  }
  return `${emoji} ${who} ${typeKo} 신청 ${qty}일 신청`;
}

/** 둘째 줄: 기간만 (일수 없음) */
function formatLeaveApprovalPeriodOnly(it) {
  const d = it.detail;
  if (!d?.start_date) {
    return String(it.summary || '—').trim() || '—';
  }
  if (d.end_date && String(d.end_date) !== String(d.start_date)) {
    return `${d.start_date} ~ ${d.end_date}`;
  }
  return String(d.start_date);
}


const PO_STATE_STYLE = {
  '대기':  { bg: '#fef3c7', color: '#92400e', label: '내 차례' },
  '예정':  { bg: '#eff6ff', color: '#1e40af', label: '결재중' },
  '승인':  { bg: '#dcfce7', color: '#166534', label: '승인완료' },
  '반려':  { bg: '#fee2e2', color: '#991b1b', label: '반려됨' },
};

function paymentStateStyle(myState, docStatus) {
  const base = PO_STATE_STYLE[myState] || { bg: '#f1f5f9', color: '#475569', label: myState || '—' };
  if (myState === '승인' && docStatus !== '결제완료') {
    return { ...base, label: '승인함' };
  }
  return base;
}

function normalizePaymentItem(raw, kind) {
  if (kind === 'quote') {
    return {
      ...raw,
      kind: 'quote',
      doc_seq: Number(raw.quote_seq || 0),
      doc_status: String(raw.quote_status || '').trim(),
    };
  }
  if (kind === 'order') {
    return {
      ...raw,
      kind: 'order',
      doc_seq: Number(raw.order_seq || 0),
      doc_status: String(raw.order_status || '').trim(),
    };
  }
  return {
    ...raw,
    kind: 'purchase_order',
    doc_seq: Number(raw.po_seq || 0),
    doc_status: String(raw.po_status || '').trim(),
  };
}

function paymentApiBase(item) {
  if (item.kind === 'quote') return 'quote';
  if (item.kind === 'order') return 'order';
  return 'purchase_order';
}

function paymentKindLabel(kind) {
  if (kind === 'quote') return '견적서';
  if (kind === 'order') return '주문서';
  if (kind === 'purchase_order') return '구매발주';
  return '';
}

function paymentDocLabel(kind) {
  if (kind === 'quote') return '견적서';
  if (kind === 'order') return '주문서';
  return '구매발주';
}

function paymentPreviewLabel(kind) {
  if (kind === 'quote') return '견적서 미리보기';
  if (kind === 'order') return '주문서 미리보기';
  return '구매발주서 미리보기';
}

function canPreviewPaymentItem(item) {
  return ['purchase_order', 'quote', 'order'].includes(item?.kind) && Number(item?.doc_seq || 0) > 0;
}

export default function DashboardApprovalPanel({ onHasPendingChange, refreshToken = 0 }) {
  const { user } = useAuth();
  const token = user?.token ? String(user.token) : '';
  const myEmail = String(user?.email || '').trim().toLowerCase();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [acting, setActing] = useState(null);

  // 구매발주·견적서 내결제 리스트
  const [paymentItems, setPaymentItems] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [paymentErr, setPaymentErr] = useState('');

  // 미리보기 모달: { kind, seq }
  const [previewTarget, setPreviewTarget] = useState(null);
  const [previewActing, setPreviewActing] = useState(false);
  const [expandOpen, setExpandOpen] = useState(false);
  /** 펼쳐보기에서 선택된 문서 { kind, seq } */
  const [expandFocus, setExpandFocus] = useState(null);
  const modalRef = useRef(null);

  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const fetchOpts = useMemo(() => ({
    headers: authHeaders,
    credentials: 'include',
  }), [authHeaders]);
  // 같은 요청이 겹쳐 나가지 않도록 하되, 겹친 호출은 버리지 않고 끝난 뒤 한 번 다시 돌린다.
  const loadingRef = useRef(false);
  const loadQueuedRef = useRef(false);
  const loadFnRef = useRef(null);
  const paymentLoadingRef = useRef(false);
  const paymentQueuedRef = useRef(false);
  const paymentFnRef = useRef(null);

  const formatLoadError = (status, data) => {
    const d = data?.detail ?? data?.message;
    const s = String(d || '').trim();
    if (status === 404 || s === 'Not Found') {
      return `결재함 API에 연결되지 않았습니다(HTTP ${status}). ① web 컨테이너에 레포 nginx.conf 마운트 후 web 재기동 ② entire-backend·erp-backend 최신 이미지 배포`;
    }
    if (status === 502 || status === 504) {
      return `ERP 백엔드에 연결할 수 없습니다(HTTP ${status}). erp_backend 컨테이너·네트워크를 확인하세요.`;
    }
    return s || `요청 실패 (HTTP ${status})`;
  };

  /**
   * 응답을 읽어 JSON으로 파싱한다.
   * 서버가 200 헤더를 보낸 뒤 본문이 끊기면 파싱만 실패하므로,
   * 상태코드 오류와 본문 손상을 구분해 한 번 재시도한다.
   */
  const fetchApprovalJson = useCallback(async (path) => {
    let lastTruncated = false;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const sep = path.includes('?') ? '&' : '?';
      const res = await fetch(approvalUrl(`${path}${sep}_=${Date.now()}`), fetchOpts);
      const text = await res.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch (_) {
        data = null;
      }
      if (data === null) {
        if (!res.ok) throw new Error(formatLoadError(res.status, {}));
        lastTruncated = true;
        if (attempt === 0) {
          await new Promise((r) => setTimeout(r, 700));
          continue;
        }
        break;
      }
      if (!res.ok || !data.ok) {
        throw new Error(formatLoadError(res.status, data));
      }
      return data;
    }
    if (lastTruncated) {
      throw new Error('서버 응답이 중간에 끊겼습니다. 잠시 후 다시 시도해 주세요.');
    }
    throw new Error('결재 목록을 불러오지 못했습니다.');
  }, [fetchOpts]);

  const load = useCallback(async () => {
    if (!token) {
      setItems([]);
      setLoading(false);
      return;
    }
    if (loadingRef.current) {
      loadQueuedRef.current = true;
      return;
    }
    loadingRef.current = true;
    setErr('');
    try {
      const data = await fetchApprovalJson('/inbox');
      // inbox에서 leave 건만 사용 (purchase_order·quote는 manage에서 별도 표시)
      const all = Array.isArray(data.items) ? data.items : [];
      setItems(all.filter((it) => it.kind !== 'purchase_order' && it.kind !== 'quote' && it.kind !== 'order'));
    } catch (e) {
      setErr(String(e.message || e));
      setItems([]);
    } finally {
      loadingRef.current = false;
      setLoading(false);
      if (loadQueuedRef.current) {
        loadQueuedRef.current = false;
        loadFnRef.current?.();
      }
    }
  }, [fetchApprovalJson, token]);

  const loadPayment = useCallback(async () => {
    if (!token) {
      setPaymentItems([]);
      setPaymentLoading(false);
      return;
    }
    if (paymentLoadingRef.current) {
      paymentQueuedRef.current = true;
      return;
    }
    paymentLoadingRef.current = true;
    setPaymentErr('');
    try {
      const [poData, quoteData, orderData] = await Promise.all([
        fetchApprovalJson('/purchase_order/manage'),
        fetchApprovalJson('/quote/manage'),
        fetchApprovalJson('/order/manage'),
      ]);
      const poPending = (Array.isArray(poData.items) ? poData.items : [])
        .filter((it) => String(it.my_state || '') === '대기')
        .map((it) => normalizePaymentItem(it, 'purchase_order'));
      const quotePending = (Array.isArray(quoteData.items) ? quoteData.items : [])
        .filter((it) => String(it.my_state || '') === '대기')
        .map((it) => normalizePaymentItem(it, 'quote'));
      const orderPending = (Array.isArray(orderData.items) ? orderData.items : [])
        .filter((it) => String(it.my_state || '') === '대기')
        .map((it) => normalizePaymentItem(it, 'order'));
      setPaymentItems([...poPending, ...quotePending, ...orderPending]);
    } catch (e) {
      setPaymentErr(String(e.message || e));
      setPaymentItems([]);
    } finally {
      paymentLoadingRef.current = false;
      setPaymentLoading(false);
      if (paymentQueuedRef.current) {
        paymentQueuedRef.current = false;
        paymentFnRef.current?.();
      }
    }
  }, [fetchApprovalJson, token]);

  useEffect(() => {
    loadFnRef.current = load;
    paymentFnRef.current = loadPayment;
  }, [load, loadPayment]);

  useEffect(() => {
    setLoading(true);
    setPaymentLoading(true);
    load();
    loadPayment();
  }, [load, loadPayment]);

  useEffect(() => {
    if (typeof onHasPendingChange !== 'function') return;
    if (loading || paymentLoading) return;
    const hasPending = items.length > 0 || paymentItems.length > 0;
    onHasPendingChange(hasPending);
  }, [loading, paymentLoading, items.length, paymentItems.length, onHasPendingChange]);

  useEffect(() => {
    if (!refreshToken) return;
    setLoading(true);
    setPaymentLoading(true);
    load();
    loadPayment();
  }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDecideError = (raw) => {
    const s = String(raw || '').trim();
    if (s === 'leave_not_pending') {
      return '이미 취소·승인·반려된 연차 신청입니다. 목록을 새로고침합니다.';
    }
    if (s === 'leave_not_found') return '연차 신청 정보를 찾을 수 없습니다.';
    if (s === 'not_pending') return '이미 처리된 결재 건입니다.';
    if (s === 'not_approver') return '결재 권한이 없습니다.';
    return s || '처리에 실패했습니다.';
  };

  const decide = async (taskSeq, action) => {
    if (!token || acting) return;
    let note = '';
    if (action === 'reject') {
      note = window.prompt('반려 사유를 입력하세요 (선택)') || '';
    }
    setActing(taskSeq);
    setErr('');
    try {
      const res = await fetch(approvalUrl(`/tasks/${taskSeq}/decide`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ action, note }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.detail || data.message || `HTTP ${res.status}`);
      }
      if (data.cleaned) {
        window.alert(data.message || '이미 처리·취소된 연차 신청입니다. 결재함에서 제거했습니다.');
      }
      await Promise.all([load(), loadPayment()]);
    } catch (e) {
      const msg = formatDecideError(e.message || e);
      window.alert(msg);
      // 목록은 유지한 채 새로고침 (err로 목록을 가리지 않음)
      await Promise.all([load(), loadPayment()]);
    } finally {
      setActing(null);
    }
  };

  // taskSeq가 없으면(notask) approve_direct 엔드포인트 사용
  const decidePayment = async (item, action) => {
    if (!token || acting) return;
    const docSeq = Number(item?.doc_seq || 0);
    const taskSeq = Number(item?.task_seq || 0);
    let note = '';
    if (action === 'reject') {
      note = window.prompt('반려 사유를 입력하세요 (선택)') || '';
    }
    const actingKey = `${item.kind || 'purchase_order'}-${docSeq || taskSeq}`;
    setActing(actingKey);
    setPaymentErr('');
    try {
      const url = taskSeq
        ? approvalUrl(`/tasks/${taskSeq}/decide`)
        : approvalUrl(`/${paymentApiBase(item)}/${docSeq}/approve_direct`);
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ action, note }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.detail || data.message || `HTTP ${res.status}`);
      }
      await loadPayment();
    } catch (e) {
      setPaymentErr(String(e.message || e));
    } finally {
      setActing(null);
    }
  };

  const openPreview = useCallback((item) => {
    if (!canPreviewPaymentItem(item)) return;
    setPreviewTarget({ kind: item.kind || 'purchase_order', seq: item.doc_seq });
    setPreviewActing(false);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewTarget(null);
    setPreviewActing(false);
  }, []);

  // 모달 내에서 결제/반려
  const decidePaymentModal = useCallback(async (item, action) => {
    if (!token || previewActing || !item) return;
    let note = '';
    if (action === 'reject') {
      note = window.prompt('반려 사유를 입력하세요 (선택)') || '';
    }
    setPreviewActing(true);
    try {
      const docSeq = Number(item.doc_seq || 0);
      const taskSeq = Number(item.task_seq || 0);
      const url = taskSeq
        ? approvalUrl(`/tasks/${taskSeq}/decide`)
        : approvalUrl(`/${paymentApiBase(item)}/${docSeq}/approve_direct`);
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ action, note }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.detail || data.message || `HTTP ${res.status}`);
      closePreview();
      await loadPayment();
    } catch (e) {
      alert(String(e.message || e));
    } finally {
      setPreviewActing(false);
    }
  }, [token, previewActing, authHeaders, closePreview, loadPayment]);

  const closeExpand = useCallback(() => {
    setExpandOpen(false);
    setExpandFocus(null);
  }, []);

  const openExpand = useCallback(() => {
    setExpandOpen(true);
    setPreviewTarget(null);
  }, []);

  const paymentItemKey = useCallback((it) => (
    `${it?.kind || 'purchase_order'}-${it?.doc_seq || it?.task_seq || ''}`
  ), []);

  const focusPaymentItem = useCallback((it) => {
    if (!canPreviewPaymentItem(it)) {
      setExpandFocus(null);
      return;
    }
    setExpandFocus({ kind: it.kind || 'purchase_order', seq: it.doc_seq });
  }, []);

  // 펼쳐보기 열릴 때 / 목록 갱신 시 선택 유지·자동 선택
  useEffect(() => {
    if (!expandOpen) return;
    if (!paymentItems.length) {
      setExpandFocus(null);
      return;
    }
    const still = expandFocus
      && paymentItems.some(
        (x) => x.kind === expandFocus.kind && String(x.doc_seq) === String(expandFocus.seq),
      );
    if (still) return;
    const first = paymentItems.find((x) => canPreviewPaymentItem(x)) || paymentItems[0];
    focusPaymentItem(first);
  }, [expandOpen, paymentItems, expandFocus, focusPaymentItem]);

  // ESC 키로 모달 닫기 (미리보기 우선, 없으면 펼쳐보기)
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (previewTarget) {
        closePreview();
        return;
      }
      if (expandOpen) closeExpand();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [previewTarget, expandOpen, closePreview, closeExpand]);

  const handleRefresh = () => {
    setLoading(true);
    setPaymentLoading(true);
    load();
    loadPayment();
  };

  const expandFocusItem = useMemo(() => {
    if (!expandFocus) return null;
    return paymentItems.find(
      (x) => x.kind === expandFocus.kind && String(x.doc_seq) === String(expandFocus.seq),
    ) || null;
  }, [expandFocus, paymentItems]);

  const renderPaymentCard = (it, { expanded = false } = {}) => {
    const st = paymentStateStyle(it.my_state, it.doc_status);
    const isPending = String(it.my_state || '') === '대기';
    const actingKey = paymentItemKey(it);
    const kindLabel = paymentKindLabel(it.kind);
    const docLabel = paymentDocLabel(it.kind);
    const previewable = canPreviewPaymentItem(it);
    const selected = expanded
      && expandFocus
      && it.kind === expandFocus.kind
      && String(it.doc_seq) === String(expandFocus.seq);
    return (
      <li
        key={actingKey}
        className={[
          'dap-item',
          expanded ? 'dap-expand-card' : '',
          expanded && previewable ? 'dap-expand-card--clickable' : '',
          selected ? 'dap-expand-card--selected' : '',
        ].filter(Boolean).join(' ')}
        style={{
          borderLeft: selected ? '5px solid #db2777' : `3px solid ${st.color}`,
          cursor: previewable || expanded ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (expanded) {
            focusPaymentItem(it);
            return;
          }
          if (previewable) openPreview(it);
        }}
        title={expanded
          ? '클릭하여 문서 미리보기'
          : (previewable ? `클릭하여 ${paymentPreviewLabel(it.kind)}` : undefined)}
      >
        <div className="dap-item__badges">
          <span className="dap-badge">{kindLabel}</span>
          <span className="dap-badge" style={{ background: st.bg, color: st.color }}>{st.label}</span>
        </div>
        <div className="dap-item__title" style={{ fontSize: expanded ? 14 : 13 }}>
          {String(it.issue_no || '').trim() || `${docLabel} #${it.doc_seq}`}
          {it.requester_name ? <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 6, fontSize: 12 }}>({it.requester_name})</span> : null}
        </div>
        <div className="dap-item__summary" style={{ fontSize: expanded ? 13 : 12 }}>
          {[it.vendor_name, it.summary || it.detail?.items_summary].filter(Boolean).join(' / ') || '—'}
        </div>
        {it.ship_to ? (
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
            📁 {it.ship_to}
          </div>
        ) : null}
        {isPending ? (
          <div className="dap-item__actions" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="dap-btn dap-btn--ok"
              disabled={acting === actingKey}
              onClick={() => decidePayment(it, 'approve')}
            >
              {acting === actingKey ? '처리중…' : '결제'}
            </button>
            <button
              type="button"
              className="dap-btn dap-btn--no"
              disabled={acting === actingKey}
              onClick={() => decidePayment(it, 'reject')}
            >
              반려
            </button>
          </div>
        ) : null}
      </li>
    );
  };

  return (
    <>
    <div className="dap-panel">
      <div className="dap-panel__head">
        <h3 className="dap-panel__title">내 결재</h3>
        <div className="dap-panel__head-actions">
          <button
            type="button"
            className="dap-panel__expand"
            onClick={openExpand}
            title="내결제 펼쳐보기"
            aria-label="내결제 펼쳐보기"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button type="button" className="dap-panel__refresh" onClick={handleRefresh} disabled={loading || paymentLoading}>
            새로고침
          </button>
        </div>
      </div>

      {/* ── 구매발주·견적서·주문서 내결제 리스트 ── */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#1d4ed8', marginBottom: 6, letterSpacing: 0.2 }}>
          구매·견적·주문서 내결제
        </div>
        {paymentErr ? (
          <div className="dap-panel__err" style={{ fontSize: 12 }}>{paymentErr}</div>
        ) : paymentLoading ? (
          <div className="dap-panel__muted" style={{ fontSize: 12 }}>불러오는 중…</div>
        ) : paymentItems.length === 0 ? (
          <div className="dap-panel__empty" style={{ fontSize: 12 }}>구매발주·견적서·주문서 결제 건이 없습니다.</div>
        ) : (
          <ul className="dap-list" style={{ margin: 0, padding: 0 }}>
            {paymentItems.map((it) => renderPaymentCard(it))}
          </ul>
        )}
      </div>

      {/* ── 연차·휴가 결재 대기 ── */}
      <div style={{ fontSize: 12, fontWeight: 800, color: '#7c3aed', marginBottom: 6, letterSpacing: 0.2 }}>
        연차·휴가 결재
      </div>
      <p className="dap-panel__sub" style={{ marginTop: 0 }}>나에게 올라온 연차·휴가 결재 대기 건입니다.</p>
      {err ? <div className="dap-panel__err">{err}</div> : null}
      {loading ? (
        <div className="dap-panel__muted">불러오는 중…</div>
      ) : err ? null : items.length === 0 ? (
        <div className="dap-panel__empty">연차·휴가 결재 대기 건이 없습니다.</div>
      ) : (
        <ul className="dap-list">
          {items.map((it) => (
            <li key={it.seq} className="dap-item">
              <div className="dap-item__badges">
                <span className="dap-badge">{KIND_LABEL[it.kind] || paymentKindLabel(it.kind) || '—'}</span>
                <span className="dap-badge dap-badge--wait">대기</span>
              </div>
              <div className="dap-item__title">
                {it.kind === 'leave'
                  ? formatLeaveApprovalHeadline(it)
                  : it.title}
              </div>
              <div className="dap-item__summary">
                {it.kind === 'leave'
                  ? formatLeaveApprovalPeriodOnly(it)
                  : it.summary}
              </div>
              <div className="dap-item__actions">
                <button
                  type="button"
                  className="dap-btn dap-btn--ok"
                  disabled={acting === it.seq}
                  onClick={() => decide(it.seq, 'approve')}
                >
                  승인
                </button>
                <button
                  type="button"
                  className="dap-btn dap-btn--no"
                  disabled={acting === it.seq}
                  onClick={() => decide(it.seq, 'reject')}
                >
                  반려
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>

    {/* ── 내결제 펼쳐보기 모달 (왼쪽 문서 / 오른쪽 결제 목록) ── */}
    {expandOpen ? (() => {
      const focusItem = expandFocusItem;
      const focusPending = focusItem && String(focusItem.my_state || '') === '대기';
      const focusNotMe = focusItem
        ? String(focusItem.requester_email || '').trim().toLowerCase() !== myEmail
        : true;
      const focusActingKey = focusItem ? paymentItemKey(focusItem) : '';
      return (
        <div
          className="dap-expand-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) closeExpand(); }}
          role="presentation"
        >
          <div
            className="dap-expand-modal dap-expand-modal--split"
            role="dialog"
            aria-modal="true"
            aria-label="내결제 펼쳐보기"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dap-expand-modal__head">
              <h3 className="dap-expand-modal__title">
                구매·견적·주문서 내결제
                <span>{paymentLoading ? '불러오는 중…' : `${paymentItems.length}건`}</span>
              </h3>
              <div className="dap-panel__head-actions">
                <button
                  type="button"
                  className="dap-panel__refresh"
                  onClick={handleRefresh}
                  disabled={loading || paymentLoading}
                >
                  새로고침
                </button>
                <button
                  type="button"
                  onClick={closeExpand}
                  style={{
                    width: 32, height: 32, borderRadius: 8,
                    border: '1px solid #e2e8f0', background: '#f8fafc',
                    fontSize: 16, cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}
                  title="닫기 (ESC)"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="dap-expand-split">
              <section className="dap-expand-split__preview" aria-label="문서 미리보기">
                {paymentLoading ? (
                  <div className="dap-expand-empty">불러오는 중…</div>
                ) : !focusItem || !canPreviewPaymentItem(focusItem) ? (
                  <div className="dap-expand-empty">
                    {paymentItems.length ? '오른쪽에서 결제 건을 선택하세요.' : '결제 건이 없습니다.'}
                  </div>
                ) : (
                  <>
                    <div className="dap-expand-split__preview-head">
                      <div className="dap-expand-split__preview-title">
                        {paymentPreviewLabel(focusItem.kind)}
                        <span>
                          {String(focusItem.issue_no || '').trim()
                            || `${paymentDocLabel(focusItem.kind)} #${focusItem.doc_seq}`}
                        </span>
                      </div>
                      {focusPending && focusNotMe ? (
                        <div className="dap-expand-split__preview-actions">
                          <button
                            type="button"
                            className="dap-btn dap-btn--ok"
                            style={{ flex: 'none', minWidth: 88 }}
                            disabled={acting === focusActingKey}
                            onClick={() => decidePayment(focusItem, 'approve')}
                          >
                            {acting === focusActingKey ? '처리중…' : '✓ 결제'}
                          </button>
                          <button
                            type="button"
                            className="dap-btn dap-btn--no"
                            style={{ flex: 'none', minWidth: 72 }}
                            disabled={acting === focusActingKey}
                            onClick={() => decidePayment(focusItem, 'reject')}
                          >
                            반려
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <div className="dap-expand-split__preview-body">
                      <Suspense fallback={
                        <div className="dap-expand-empty">문서 불러오는 중…</div>
                      }>
                        <div className="dap-expand-split__preview-frame">
                          <RemotePoPreview seq={focusItem.doc_seq} kind={focusItem.kind} />
                        </div>
                      </Suspense>
                    </div>
                  </>
                )}
              </section>

              <aside className="dap-expand-split__list" aria-label="결제 목록">
                <div className="dap-expand-split__list-head">결제 대기 목록</div>
                {paymentErr ? (
                  <div className="dap-panel__err" style={{ margin: '0 12px 8px' }}>{paymentErr}</div>
                ) : null}
                {paymentLoading ? (
                  <div className="dap-expand-empty" style={{ padding: 24 }}>불러오는 중…</div>
                ) : paymentItems.length === 0 ? (
                  <div className="dap-expand-empty" style={{ padding: 24 }}>
                    구매발주·견적서·주문서 결제 건이 없습니다.
                  </div>
                ) : (
                  <ul className="dap-expand-split__cards">
                    {paymentItems.map((it) => renderPaymentCard(it, { expanded: true }))}
                  </ul>
                )}
              </aside>
            </div>
          </div>
        </div>
      );
    })() : null}

    {/* ── 구매발주·견적·주문 미리보기 모달 ── */}
    {previewTarget && (() => {
      const previewItem = paymentItems.find(
        (x) => x.kind === previewTarget.kind && String(x.doc_seq) === String(previewTarget.seq),
      );
      const isPending = previewItem && String(previewItem.my_state || '') === '대기';
      const isNotMe = previewItem
        ? String(previewItem.requester_email || '').trim().toLowerCase() !== myEmail
        : true;
      return (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(15,23,42,0.6)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '24px 12px', overflowY: 'auto',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closePreview(); }}
        >
          <div
            ref={modalRef}
            style={{
              background: '#fff', borderRadius: 18, width: '100%', maxWidth: 920,
              boxShadow: '0 24px 64px rgba(15,23,42,0.22)',
              position: 'relative', maxHeight: '92vh',
              display: 'flex', flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 모달 헤더 */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px', borderBottom: '1px solid #e2e8f0', flexShrink: 0,
            }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: '#0f172a' }}>
                {paymentPreviewLabel(previewTarget.kind)}
              </div>
              <button
                type="button"
                onClick={closePreview}
                style={{
                  width: 32, height: 32, borderRadius: 8,
                  border: '1px solid #e2e8f0', background: '#f8fafc',
                  fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                title="닫기 (ESC)"
              >✕</button>
            </div>

            {/* 문서 미리보기 (MFE) */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              background: '#f1f5f9',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              padding: '16px 12px 24px',
              boxSizing: 'border-box',
            }}>
              <Suspense fallback={
                <div style={{ padding: '40px 0', textAlign: 'center', color: '#64748b', width: '100%' }}>불러오는 중…</div>
              }>
                <div style={{ width: '100%', maxWidth: 860, margin: '0 auto' }}>
                  <RemotePoPreview seq={previewTarget.seq} kind={previewTarget.kind} />
                </div>
              </Suspense>
            </div>

            {/* 결제/반려 버튼 */}
            {isPending && isNotMe && (
              <div style={{
                display: 'flex', gap: 10, padding: '14px 20px',
                borderTop: '1px solid #e2e8f0', justifyContent: 'flex-end', flexShrink: 0,
              }}>
                <button
                  type="button"
                  disabled={previewActing}
                  onClick={() => decidePaymentModal(previewItem, 'approve')}
                  style={{
                    height: 40, padding: '0 22px', borderRadius: 10,
                    border: 'none', background: '#16a34a', color: '#fff',
                    fontWeight: 900, fontSize: 14, cursor: previewActing ? 'not-allowed' : 'pointer',
                    opacity: previewActing ? 0.6 : 1,
                  }}
                >
                  {previewActing ? '처리 중…' : '✓ 결제'}
                </button>
                <button
                  type="button"
                  disabled={previewActing}
                  onClick={() => decidePaymentModal(previewItem, 'reject')}
                  style={{
                    height: 40, padding: '0 22px', borderRadius: 10,
                    border: '1.5px solid #dc2626', background: '#fff', color: '#dc2626',
                    fontWeight: 900, fontSize: 14, cursor: previewActing ? 'not-allowed' : 'pointer',
                    opacity: previewActing ? 0.6 : 1,
                  }}
                >
                  반려
                </button>
              </div>
            )}
          </div>
        </div>
      );
    })()}
    </>
  );
}
